import React, { useState, useEffect, useRef } from 'react';
import { appPath } from '../../../utils/appPaths.js';
import Dropzone from 'react-dropzone-uploader';
import { Col, Card, CardHeader, CardBody, CardFooter, Progress, Alert } from 'reactstrap';
import { H5, Btn } from '../../../AbstractElements';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import axiosInstance from '../../../api/axios';
import { useNavigate } from 'react-router-dom';

/**
 * IncomeStepTwo Component - S3 Direct Upload
 * 
 * Handles direct file uploads to AWS S3 using pre-signed URLs
 * - Gets pre-signed URL from Income module backend
 * - Uploads file directly to S3 with proper key structure
 * - Sends completion notification to Income module
 * - Bypasses PHP upload limits completely
 * - Uses S3StorageService key structure: input/RAW/{year}/{month}/{provider}
 */
function IncomeStepTwo({ handleBack, handleNext, incomeId }) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    
    // State management
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStartTime, setUploadStartTime] = useState(null);
    const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
    const [uploadSpeed, setUploadSpeed] = useState(0);
    const [uploadedBytes, setUploadedBytes] = useState(0);
    const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
    const [s3Key, setS3Key] = useState(null);
    
    // Refs
    const abortControllerRef = useRef(null);

    const isBusy = isUploading;

    const handleFileChange = ({ meta, file }, status) => {
        if (status === 'done') {
            // Validate file type
            const allowedTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const allowedExtensions = ['csv', 'xlsx', 'xls', 'tsv'];
            
            if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
                toast.error('Please select a valid CSV or Excel file (.csv, .xlsx, .xls, .tsv)');
                return;
            }

            // Validate file size (500MB max)
            const maxSize = 500 * 1024 * 1024; // 500MB
            if (file.size > maxSize) {
                toast.error('File size too large. Maximum allowed size is 500MB.');
                return;
            }

            setFile(file);
            setUploadStatus('idle');
            setUploadProgress(0);
            
            console.log('File selected:', {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
            });
        }
    };

    // Calculate upload speed and estimated time remaining
    useEffect(() => {
        if (isUploading && uploadProgress > 0 && uploadStartTime) {
            const elapsedTime = (Date.now() - uploadStartTime) / 1000; // in seconds
            const totalEstimatedTime = (elapsedTime * 100) / uploadProgress;
            const remaining = Math.round(totalEstimatedTime - elapsedTime);
            
            // Calculate upload speed
            const speed = uploadedBytes / elapsedTime; // bytes per second
            setUploadSpeed(speed);
            
            if (remaining > 0) {
                setEstimatedTimeRemaining(remaining);
            }
        }
    }, [uploadProgress, isUploading, uploadStartTime, uploadedBytes]);

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Format upload speed
    const formatSpeed = (bytesPerSecond) => {
        if (bytesPerSecond === 0) return '0 B/s';
        const k = 1024;
        const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
        const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
        return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    /**
     * Get pre-signed URL from Laravel backend
     */
    const getPresignedUrl = async (file) => {
        try {
            console.log('Requesting pre-signed URL for Income ID:', incomeId);
            
            const response = await axiosInstance.post(`/incomes/s3/presigned/${incomeId}`, {
                file_name: file.name,
                file_size: file.size,
                file_type: getFileType(file.name)
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                timeout: 60000,
            });

            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to get pre-signed URL');
            }

            console.log('Pre-signed URL received:', {
                url: response.data.presigned_url,
                s3_key: response.data.s3_key,
                expires_in: response.data.expires_in
            });

            return response.data;
        } catch (error) {
            console.error('Error getting pre-signed URL:', error);
            
            if (error.response) {
                // Server responded with error status
                const errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
                throw new Error(errorMessage);
            } else if (error.request) {
                // Request was made but no response received
                throw new Error('No response from server. Please check your connection.');
            } else {
                // Something else happened
                throw new Error(error.message || 'Failed to get pre-signed URL');
            }
        }
    };

    /**
     * Upload file directly to S3 using pre-signed URL
     */
    const uploadToS3 = async (file, presignedUrl, contentType) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            
            // Track upload progress
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = (event.loaded / event.total) * 100;
                    setUploadProgress(Math.round(percentComplete));
                    setUploadedBytes(event.loaded);
                    
                    // Calculate upload speed and estimated time
                    if (uploadStartTime) {
                        const elapsed = (Date.now() - uploadStartTime) / 1000; // seconds
                        const speed = event.loaded / elapsed; // bytes per second
                        setUploadSpeed(speed);
                        
                        if (speed > 0) {
                            const remaining = (event.total - event.loaded) / speed;
                            setEstimatedTimeRemaining(Math.round(remaining));
                        }
                    }
                }
            });

            // Handle upload completion
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    console.log('Upload to S3 completed successfully');
                    resolve({
                        success: true,
                        status: xhr.status,
                        response: xhr.responseText
                    });
                } else {
                    console.error('Upload to S3 failed:', xhr.status, xhr.responseText);
                    reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
                }
            });

            // Handle upload errors
            xhr.addEventListener('error', () => {
                console.error('Upload to S3 error:', {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    responseText: xhr.responseText,
                    readyState: xhr.readyState
                });
                
                let errorMessage = `Upload error: ${xhr.statusText}`;
                if (xhr.status === 403) {
                    errorMessage = 'CORS error: S3 bucket CORS configuration may be incorrect. Please check S3 CORS settings.';
                } else if (xhr.status === 0) {
                    errorMessage = 'Network error: Unable to connect to S3. Please check your internet connection.';
                }
                
                reject(new Error(errorMessage));
            });

            // Handle upload abortion
            xhr.addEventListener('abort', () => {
                console.log('Upload to S3 aborted');
                reject(new Error('Upload aborted by user'));
            });

            // Configure and start upload
            xhr.open('PUT', presignedUrl);
            const resolvedType = contentType || file.type || 'application/octet-stream';
            xhr.setRequestHeader('Content-Type', resolvedType);
            
            // Store abort controller reference
            abortControllerRef.current = xhr;
            
            // Start upload
            xhr.send(file);
        });
    };

    /**
     * Notify Laravel backend about upload completion
     */
    const notifyUploadComplete = async (s3Key, fileName, fileSize, status) => {
        try {
            console.log('Notifying upload completion...', { s3Key, fileName, fileSize, status });
            
            // Validate required parameters
            if (!s3Key || s3Key.trim() === '') {
                throw new Error('S3 key is required for upload completion notification');
            }
            
            const payload = {
                s3_key: s3Key,
                file_name: fileName,
                file_size: fileSize,
                upload_status: status,
                income_id: incomeId
            };
            
            console.log('Sending upload completion payload:', payload);
            
            const response = await axiosInstance.post('/incomes/s3/upload-complete', payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                timeout: 60000,
            });

            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to notify upload completion');
            }

            console.log('Upload completion notification sent successfully');
            return response.data;
        } catch (error) {
            console.error('Error notifying upload completion:', error);
            
            if (error.response) {
                console.error('Server response error:', {
                    status: error.response.status,
                    data: error.response.data,
                    headers: error.response.headers
                });
                
                if (error.response.data?.debug_info) {
                    console.error('Debug info from server:', error.response.data.debug_info);
                }
            }

            const message =
                error.response?.data?.message ||
                error.message ||
                'Failed to register upload on server';

            throw new Error(message);
        }
    };

    /**
     * Get file type from filename
     */
    const getFileType = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        const typeMap = {
            'csv': 'csv',
            'xlsx': 'xlsx',
            'xls': 'xls',
            'tsv': 'tsv',
            'pdf': 'pdf',
        };
        return typeMap[extension] || 'csv';
    };

    /**
     * Main upload function - Direct S3 Upload
     */
    const handleUpload = async () => {
        if (!file) {
            toast.error('Please select a file first');
            return;
        }

        setIsUploading(true);
        setUploadStatus('uploading');
        setUploadProgress(0);
        setUploadStartTime(Date.now());
        setUploadSpeed(0);
        setEstimatedTimeRemaining(null);
        setUploadedBytes(0);

        let uploadCompleted = false;

        try {
            console.log('Starting direct S3 upload process...');
            
            // Step 1: Get pre-signed URL from Laravel
            const presignedData = await getPresignedUrl(file);
            console.log('Pre-signed data received:', presignedData);
            setS3Key(presignedData.s3_key);
            
            // Step 2: Upload file directly to S3
            console.log('Starting S3 upload with URL:', presignedData.presigned_url);
            const uploadResult = await uploadToS3(
                file,
                presignedData.presigned_url,
                presignedData.content_type
            );
            
            if (uploadResult.success) {
                // Step 3: Notify Laravel about successful upload
                console.log('S3 upload successful, notifying backend...', {
                    s3_key: presignedData.s3_key,
                    file_name: presignedData.file_name,
                    file_size: file.size
                });
                
                await notifyUploadComplete(
                    presignedData.s3_key,
                    presignedData.file_name || file.name,
                    file.size,
                    'success'
                );

                uploadCompleted = true;
                setUploadStatus('success');
                toast.success(
                    'Raw file uploaded. CSV processing will continue automatically in the background.'
                );

                queryClient.invalidateQueries(['incomes']);
                navigate(appPath('/income/income-list'));
                
                console.log('Direct S3 upload completed; server-side CSV processing scheduled');
            } else {
                throw new Error('Upload failed');
            }

        } catch (error) {
            console.error('Upload failed:', error);

            if (!uploadCompleted) {
                setUploadStatus('error');
                toast.error(`Upload failed: ${error.message}`);

                if (s3Key) {
                    try {
                        await notifyUploadComplete(s3Key, file.name, file.size, 'failed');
                    } catch (notifyError) {
                        console.error('Failed to notify about upload failure:', notifyError);
                    }
                }
            }
        } finally {
            setIsUploading(false);
            abortControllerRef.current = null;
        }
    };

    /**
     * Cancel ongoing upload
     */
    const cancelUpload = () => {
        if (window.confirm('Are you sure you want to cancel the upload?')) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            
            setIsUploading(false);
            setUploadStatus('idle');
            setUploadProgress(0);
            setUploadSpeed(0);
            setEstimatedTimeRemaining(null);
            setUploadedBytes(0);
            
            toast.info('Upload cancelled');
            console.log('Upload cancelled by user');
        }
    };

    /**
     * Reset form
     */
    const handleReset = () => {
        setFile(null);
        setUploadStatus('idle');
        setUploadProgress(0);
        setUploadSpeed(0);
        setEstimatedTimeRemaining(null);
        setUploadedBytes(0);
        setS3Key(null);
    };

    // Format time remaining in a human-readable format
    const formatTimeRemaining = (seconds) => {
        if (!seconds) return '';
        
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.round(seconds % 60);
        
        if (minutes > 0) {
            return `${minutes} min ${remainingSeconds} sec`;
        }
        return `${remainingSeconds} sec`;
    };

    // Warn user before leaving page during upload
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isBusy) {
                event.preventDefault();
                event.returnValue = 'Upload in progress. Are you sure you want to leave?';
                return event.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isBusy]);

    return (
        <Col sm="12">
            <Card className="card-absolute" style={{ position: 'relative', overflow: 'visible', marginTop: '10px' }}>
                <CardHeader className="bg-primary" style={{ zIndex: 1 }}>
                    <H5 style={{ color: '#ffffff', fontWeight: '600', margin: 0 }}>Raw CSV — Direct S3 Upload</H5>
                </CardHeader>
                <CardBody style={{ paddingTop: '25px' }}>
                   
                    <Dropzone
                        className="dropzone dz-clickable"
                        onChangeStatus={handleFileChange}
                        maxFiles={1}
                        multiple={false}
                        canCancel={false}
                        inputContent="Drop raw CSV / Excel file"
                        styles={{
                            dropzone: { width: '100%', height: 150 },
                            dropzoneActive: { borderColor: 'green' },
                        }}
                        disabled={isBusy}
                        maxSizeBytes={500 * 1024 * 1024} // 500MB max
                        accept=".csv, .xls, .xlsx, .tsv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv, text/tab-separated-values"
                    />
                    
                    {file && !isBusy && (
                        <div className="mt-3">
                            {file.size > 50 * 1024 * 1024 && (
                                <Alert color="warning" className="mt-2">
                                    <strong>Large file detected:</strong> This file is larger than 50MB and may take several minutes to upload.
                                    Please keep this window open until the upload completes.
                                </Alert>
                            )}
                        </div>
                    )}
                    
                    {(isUploading) && (
                        <div className="mt-3">
                            <div className="d-flex justify-content-between mb-1">
                                <span>Upload Progress to S3</span>
                                <span>{uploadProgress.toFixed(1)}%</span>
                            </div>
                            <Progress value={uploadProgress} color="primary" />
                            
                            {/* Upload Status */}
                            <div className="mt-2">
                                {uploadStatus === 'uploading' && (
                                    <div className="d-flex align-items-center text-primary mb-2">
                                        <div className="spinner-border spinner-border-sm me-2" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span>Uploading to AWS S3...</span>
                                    </div>
                                )}
                                
                                <div className="row">
                                    <div className="col-md-6">
                                        <p className="text-muted mb-1">
                                            <strong>File:</strong> {file.name}
                                        </p>
                                        <p className="text-muted mb-1">
                                            <strong>Size:</strong> {formatFileSize(file.size)}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="text-muted mb-1">
                                            <strong>Method:</strong> Direct S3 Upload
                                        </p>
                                        <p className="text-muted mb-1">
                                            <strong>Speed:</strong> {formatSpeed(uploadSpeed)}
                                        </p>
                                    </div>
                                </div>
                                
                                {estimatedTimeRemaining && uploadProgress < 100 && (
                                    <p className="text-muted mb-2">
                                        <strong>Estimated time remaining:</strong> {formatTimeRemaining(estimatedTimeRemaining)}
                                    </p>
                                )}
                                
                                {s3Key && (
                                    <p className="text-info mb-1">
                                        <strong>S3 Key:</strong> {s3Key}
                                    </p>
                                )}
                                
                                <p className="text-muted mb-0 mt-2">
                                    <strong>Please keep this window open until the upload completes.</strong>
                                </p>
                            </div>
                        </div>
                    )}
                </CardBody>
                <CardFooter className="text-end">
                    {isBusy ? (
                        <Btn
                            attrBtn={{
                                color: 'danger',
                                className: 'm-r-15',
                                onClick: cancelUpload,
                            }}
                        >
                            Cancel Upload
                        </Btn>
                    ) : (
                        <>
                            <Btn
                                attrBtn={{
                                    color: 'secondary',
                                    className: 'm-r-15',
                                    onClick: handleBack,
                                }}
                            >
                                Back
                            </Btn>
                            {file && (
                                <Btn
                                    attrBtn={{
                                        color: 'warning',
                                        className: 'm-r-15',
                                        onClick: handleReset,
                                    }}
                                >
                                    Reset
                                </Btn>
                            )}
                            <Btn
                                attrBtn={{
                                    color: 'primary',
                                    className: 'm-r-15',
                                    onClick: handleUpload,
                                    disabled: !file || isBusy,
                                }}
                            >
                                Upload &amp; Process CSV
                            </Btn>
                        </>
                    )}
                </CardFooter>
            </Card>
        </Col>
    );
}

export default IncomeStepTwo;
